import * as React from 'react';

// =============================================================================
// operational-view-preferences.ts — Préférences d'AFFICHAGE §2.5 v2.2 (Cx-6 / SF §503).
// =============================================================================
// DÉCISION GATE_0 S14 (G0-S14-5) — VERBATIM : « Une préférence d'AFFICHAGE ≠ donnée métier. La règle
// "zéro DONNÉE MÉTIER en localStorage" N'EST PAS violée (seules des préférences d'affichage y vont). »
//
// PÉRIMÈTRE STRICT de ce qui est persisté (Cx-6, S14-bis) :
//   • colonnes MASQUÉES (ids de colonnes de la Section 2) ;
//   • TRI courant ({ id, desc } | null).
// NE SONT PAS persistés (décision GATE_HUMAN S14-bis C3) : les FILTRES (recherche raison sociale, profil,
//   toggle « à actualiser ») — ce sont des recherches EN COURS, transients (re-saisis par session), pas des
//   préférences d'affichage au sens §503. La Section 3 (détail dynamique) est triable/masquable mais NON
//   persistée (état éphémère re-peuplé à chaque sélection).
//
// MIGRATION NON-DESTRUCTIVE v1 → v2 : si la clé v2 d'une page est absente, on LIT une fois l'ancienne clé
// v1 (`jeko.a15.display-preferences.v1`), on REPORTE les colonnes masquées encore valides (intersection avec
// les colonnes connues de la page), on IGNORE la partie filtres de v1, et le tri démarre vide. v1 n'est
// JAMAIS réécrite (pas d'écrasement). Tout parse est FAIL-SAFE : JSON invalide / clé manquante / type
// inattendu ⇒ DÉFAUT, jamais un throw, jamais un vidage brutal (la page MONTE toujours).
//
// RULE_REF_006 (no-restricted-properties sur `localStorage`) cible les DONNÉES MÉTIER : l'unique accès
// `localStorage` du repo est ISOLÉ ICI, avec un eslint-disable JUSTIFIÉ par G0-S14-5. Accès défensif
// (typeof window guard + try/catch) : un navigateur sans storage dégrade en in-memory.

/** Pages §2.5 v2.2 portant des préférences d'affichage persistées. */
export type OperationalViewPage = 'profils' | 'fournisseurs';

/** Tri d'une colonne (id de colonne + sens). */
export interface ColumnSort {
  readonly id: string;
  readonly desc: boolean;
}

/** Préférences d'affichage persistées (UNIQUEMENT colonnes masquées + tri — jamais de filtre/métier). */
export interface DisplayPreferences {
  /** Ids des colonnes MASQUÉES (Section 2). Absence ⇒ colonne visible. */
  readonly hiddenColumns: readonly string[];
  /** Tri courant de la Section 2 ; null = ordre par défaut. */
  readonly sort: ColumnSort | null;
}

/** Préférences par défaut : tout visible, aucun tri. */
export const DEFAULT_PREFERENCES: DisplayPreferences = {
  hiddenColumns: [],
  sort: null,
};

/** Clé v2 d'une page (versionnée + scopée par page : Profils / Fournisseurs ont des colonnes distinctes). */
function storageKey(page: OperationalViewPage): string {
  return `jeko.opv.${page}.v2`;
}

/** Ancienne clé v1 (écran « Vue opérationnelle » retiré en C1) — lue UNE FOIS pour migration, jamais écrite. */
const LEGACY_V1_KEY = 'jeko.a15.display-preferences.v1';

/** Garde-fou : un id de colonne est une chaîne non vide connue de la page. */
function sanitizeHidden(
  raw: unknown,
  knownColumnIds: readonly string[],
): readonly string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const known = new Set(knownColumnIds);
  return raw.filter(
    (id): id is string => typeof id === 'string' && known.has(id),
  );
}

/** Parse défensif d'un tri ({ id: string connu, desc: boolean }) ; null si invalide. */
function sanitizeSort(
  raw: unknown,
  knownColumnIds: readonly string[],
): ColumnSort | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const id = obj['id'];
  const desc = obj['desc'];
  if (
    typeof id === 'string' &&
    knownColumnIds.includes(id) &&
    typeof desc === 'boolean'
  ) {
    return { id, desc };
  }
  return null;
}

/** Parse FAIL-SAFE d'une valeur v2 inconnue → DisplayPreferences (défaut si illisible). */
function parseV2(
  raw: unknown,
  knownColumnIds: readonly string[],
): DisplayPreferences {
  if (typeof raw !== 'object' || raw === null) {
    return DEFAULT_PREFERENCES;
  }
  const obj = raw as Record<string, unknown>;
  return {
    hiddenColumns: sanitizeHidden(obj['hiddenColumns'], knownColumnIds),
    sort: sanitizeSort(obj['sort'], knownColumnIds),
  };
}

/**
 * Migration NON-DESTRUCTIVE depuis v1 : reporte UNIQUEMENT `hiddenColumns` (intersection avec les colonnes
 * connues de la page), IGNORE les filtres v1 (cycle/profil/séniorité), tri vide. Fail-safe → défaut.
 */
function migrateFromV1(
  raw: unknown,
  knownColumnIds: readonly string[],
): DisplayPreferences {
  if (typeof raw !== 'object' || raw === null) {
    return DEFAULT_PREFERENCES;
  }
  const obj = raw as Record<string, unknown>;
  return {
    hiddenColumns: sanitizeHidden(obj['hiddenColumns'], knownColumnIds),
    sort: null,
  };
}

/** Lit `key` du localStorage et le parse en JSON ; null si absent / illisible / storage indisponible. */
function readJson(key: string): unknown {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    // G0-S14-5 : préférence d'AFFICHAGE, PAS une donnée métier. SEUL accès stockage du repo (lecture).
    // eslint-disable-next-line no-restricted-properties
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    // JSON invalide / storage indisponible : on REMONTE null ⇒ l'appelant retombe sur le défaut.
    return null;
  }
}

/**
 * Charge les préférences d'affichage d'une page (fail-safe). Ordre : v2 de la page → sinon migration v1 →
 * sinon défaut. `knownColumnIds` borne les ids reportés (colonnes inconnues ignorées). NE crash JAMAIS.
 */
export function loadPreferences(
  page: OperationalViewPage,
  knownColumnIds: readonly string[],
): DisplayPreferences {
  const v2 = readJson(storageKey(page));
  if (v2 !== null) {
    return parseV2(v2, knownColumnIds);
  }
  const v1 = readJson(LEGACY_V1_KEY);
  if (v1 !== null) {
    return migrateFromV1(v1, knownColumnIds);
  }
  return DEFAULT_PREFERENCES;
}

/** Écrit les préférences v2 d'une page (no-op si storage indisponible). N'écrit JAMAIS la clé v1. */
export function savePreferences(
  page: OperationalViewPage,
  prefs: DisplayPreferences,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    // G0-S14-5 : préférence d'AFFICHAGE seulement. SEUL accès stockage du repo (écriture), clé v2 de la page.
    // eslint-disable-next-line no-restricted-properties
    window.localStorage.setItem(storageKey(page), JSON.stringify(prefs));
  } catch {
    // Storage indisponible (mode privé strict / quota) : dégrade silencieusement en in-memory.
  }
}

/**
 * Hook : préférences d'affichage persistées d'une page (chargées une fois, sauvegardées à chaque changement).
 * Source de vérité = état React in-memory ; le localStorage n'est qu'un MIROIR de préférence d'affichage.
 */
export function useDisplayPreferences(
  page: OperationalViewPage,
  knownColumnIds: readonly string[],
): readonly [DisplayPreferences, (next: DisplayPreferences) => void] {
  const [prefs, setPrefs] = React.useState<DisplayPreferences>(() =>
    loadPreferences(page, knownColumnIds),
  );
  const update = React.useCallback(
    (next: DisplayPreferences): void => {
      setPrefs(next);
      savePreferences(page, next);
    },
    [page],
  );
  return [prefs, update] as const;
}
